import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Bug,
  CheckCircle2,
  AlertTriangle,
  Loader2,
  Send,
  HelpCircle,
} from 'lucide-react';
import { BugDropzone } from './BugDropzone';
import { bugService } from '@/services/bugService';
import { getDeviceContext, extractRouteContext } from '@/lib/deviceContext';
import { BugReportRecord } from '@/types/database';

interface BugReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedFarmId?: string | null;
  contextHint?: { entity_type?: string; entity_id?: string };
}

export const BugReportModal: React.FC<BugReportModalProps> = ({
  isOpen,
  onClose,
  selectedFarmId,
  contextHint,
}) => {
  const location = useLocation();

  const [description, setDescription] = useState<string>('');
  const [attemptedAction, setAttemptedAction] = useState<string>('');
  const [files, setFiles] = useState<File[]>([]);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [submittedBug, setSubmittedBug] = useState<BugReportRecord | null>(null);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setDescription('');
      setAttemptedAction('');
      setFiles([]);
      setError(null);
      setSubmittedBug(null);
    }
  }, [isOpen]);

  // Global Dialog Paste Listener for Desktop Clipboard Snipping
  const handleDialogPaste = useCallback(
    (e: React.ClipboardEvent<HTMLDivElement>) => {
      const items = e.clipboardData?.items;
      if (!items) return;

      const imageItems: DataTransferItem[] = [];
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item.type.startsWith('image/')) {
          imageItems.push(item);
        }
      }

      // If an image was pasted, process it as a screenshot attachment
      if (imageItems.length > 0) {
        e.preventDefault();
        const newFiles: File[] = [];

        for (const item of imageItems) {
          const blob = item.getAsFile();
          if (blob) {
            const ext = blob.type.split('/')[1] || 'png';
            const timestamp = Date.now();
            const fileName = `screenshot_${timestamp}_${files.length + newFiles.length + 1}.${ext}`;
            const file = new File([blob], fileName, { type: blob.type });
            newFiles.push(file);
          }
        }

        if (newFiles.length > 0) {
          const maxFiles = 5;
          if (files.length + newFiles.length > maxFiles) {
            setError(`Maximum ${maxFiles} screenshot attachments allowed.`);
            const available = Math.max(0, maxFiles - files.length);
            setFiles((prev) => [...prev, ...newFiles.slice(0, available)]);
          } else {
            setFiles((prev) => [...prev, ...newFiles]);
          }
        }
      }
      // If pure text, let the standard input/textarea paste proceed normally
    },
    [files]
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!description.trim()) {
      setError('Please describe what happened before submitting.');
      return;
    }

    setIsSubmitting(true);

    try {
      // Diagnostic context extraction
      const currentRoute = location.pathname + location.search;
      const routeInfo = extractRouteContext(location.pathname);
      const entity_type = contextHint?.entity_type || routeInfo.entity_type || null;
      const entity_id = contextHint?.entity_id || routeInfo.entity_id || null;
      const device_info = getDeviceContext();

      // Combined full user description if attempted action was specified
      let fullDescription = description.trim();
      if (attemptedAction.trim()) {
        fullDescription = `${fullDescription}\n\n[What I was trying to do]: ${attemptedAction.trim()}`;
      }

      const created = await bugService.submitBugReport({
        description: fullDescription,
        app_route: currentRoute,
        farm_id: selectedFarmId || null,
        entity_type,
        entity_id,
        device_info,
        attachments: files,
      });

      setSubmittedBug(created);
    } catch (err: any) {
      console.error('Failed to submit bug report:', err);
      setError(
        err.message || 'Could not submit your bug report. Please check your connection and try again.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        onPaste={handleDialogPaste}
        className="max-w-lg w-full max-h-[90vh] overflow-y-auto p-4 sm:p-6"
      >
        {/* Success Confirmation State */}
        {submittedBug ? (
          <div className="py-6 flex flex-col items-center text-center space-y-4">
            <div className="w-14 h-14 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center shadow-xs animate-in zoom-in-75 duration-200">
              <CheckCircle2 className="w-8 h-8" />
            </div>

            <div className="space-y-1.5">
              <span className="inline-block px-3 py-1 rounded-full text-xs font-mono font-extrabold bg-emerald-50 text-emerald-800 border border-emerald-200">
                {submittedBug.bug_number}
              </span>
              <h2 className="text-lg font-bold text-slate-900">
                Bug {submittedBug.bug_number} has been reported.
              </h2>
              <p className="text-xs text-slate-600 max-w-sm mx-auto leading-relaxed">
                Thank you for helping us improve VSB Farm! Our team has received the diagnostic details and will investigate the issue.
              </p>
            </div>

            <Button
              type="button"
              onClick={onClose}
              className="mt-2 w-full sm:w-auto bg-slate-900 hover:bg-slate-800 font-semibold px-8"
            >
              Done
            </Button>
          </div>
        ) : (
          /* Normal Bug Reporting Form */
          <form onSubmit={handleSubmit} className="space-y-4">
            <DialogHeader className="space-y-1">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-red-100 text-red-700 flex items-center justify-center font-bold">
                  <Bug className="w-4 h-4" />
                </div>
                <div>
                  <DialogTitle className="text-base sm:text-lg font-bold text-slate-900">
                    Report a Problem
                  </DialogTitle>
                  <DialogDescription className="text-xs text-slate-500">
                    Found an issue? Tell us what happened and attach a screenshot if you have one.
                  </DialogDescription>
                </div>
              </div>
            </DialogHeader>

            {/* Error banner */}
            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-xs text-red-700 font-medium flex items-center gap-2 animate-in fade-in">
                <AlertTriangle className="w-4 h-4 shrink-0" />
                <span>{error}</span>
              </div>
            )}

            {/* Field 1: What happened? (Required) */}
            <div className="space-y-1.5">
              <Label htmlFor="bug-description" className="text-xs font-bold text-slate-800 flex items-center gap-1">
                <span>What happened?</span>
                <span className="text-red-500">*</span>
              </Label>
              <textarea
                id="bug-description"
                rows={3}
                required
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Describe what went wrong, what you saw, or any unexpected numbers/behavior..."
                className="w-full text-xs sm:text-sm p-3 rounded-lg border border-slate-300 focus:border-emerald-600 focus:ring-1 focus:ring-emerald-600 text-slate-900 placeholder:text-slate-400 resize-none transition-all"
                disabled={isSubmitting}
              />
            </div>

            {/* Field 2: What were you trying to do? (Optional) */}
            <div className="space-y-1">
              <Label htmlFor="bug-action" className="text-xs font-semibold text-slate-700">
                What were you trying to do? <span className="text-[11px] font-normal text-slate-400">(Optional)</span>
              </Label>
              <input
                id="bug-action"
                type="text"
                value={attemptedAction}
                onChange={(e) => setAttemptedAction(e.target.value)}
                placeholder="e.g. Finalize grower settlement for Cycle 2024-04"
                className="w-full text-xs p-2.5 rounded-lg border border-slate-300 focus:border-emerald-600 focus:ring-1 focus:ring-emerald-600 text-slate-900 placeholder:text-slate-400 transition-all"
                disabled={isSubmitting}
              />
            </div>

            {/* Field 3: Screenshots Dropzone & Clipboard Paste */}
            <div className="space-y-1.5 pt-1">
              <Label className="text-xs font-semibold text-slate-700">
                Add Screenshots <span className="text-[11px] font-normal text-slate-400">(Optional)</span>
              </Label>
              <BugDropzone
                files={files}
                onFilesChange={setFiles}
                maxFiles={5}
                maxSizeMb={10}
              />
            </div>

            {/* Footer Actions */}
            <DialogFooter className="pt-2 flex flex-col-reverse sm:flex-row sm:justify-end gap-2 border-t border-slate-100">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={isSubmitting}
                className="text-xs font-semibold"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting || !description.trim()}
                className="bg-emerald-700 hover:bg-emerald-600 text-white font-semibold text-xs gap-1.5 shadow-sm"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-3.5 h-3.5 animate-spin" /> Submitting...
                  </>
                ) : (
                  <>
                    <Send className="w-3.5 h-3.5" /> Submit Bug Report
                  </>
                )}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
